import type { Chat, ClientConfig, Message, UploadFile } from '@liniya/api-client';
import { parseMessageBody, referenceExcerpt } from '@liniya/api-client';
import { MessageCircleMore, Trash2, Upload, X } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { ChatHeader } from './ChatHeader';
import { MessageComposer } from './MessageComposer';
import { MessageContextMenu, type MenuTarget } from './MessageContextMenu';
import { MessageList } from './MessageList';
import { useAttachFile } from './useAttachFile';
import { useFileDrop } from './useFileDrop';
import { ConfirmDialog } from '@/components/Modal';
import { ErrorBoundary } from '@/app/ErrorBoundary';
import { useComposerStore } from '@liniya/client-core';
import { usePendingStore } from '@liniya/client-core';
import { notify, useUiStore } from '@liniya/client-core';
import { useRealtime } from '@/websocket/provider';
import {
  flattenHistory,
  markChatRead,
  useDeleteMessage,
  useDeleteMessages,
  useMessageSelection,
  useEditMessage,
  useMessageHistory,
  useReportMessage,
  useToggleReaction,
} from '@liniya/client-core';
import { useRetryMessage, useSendMessage } from '@liniya/client-core';

interface Props {
  chat: Chat;
  viewerUserId: string;
  deviceId: string;
  limits: ClientConfig['limits'] | undefined;
  onBack: () => void;
  onStartCall: (kind: 'audio' | 'video') => void;
  onOpenDetails: () => void;
}

export function ConversationView({
  chat,
  viewerUserId,
  deviceId,
  limits,
  onBack,
  onStartCall,
  onOpenDetails,
}: Props) {
  const queryClient = useQueryClient();
  const history = useMessageHistory(chat.id);
  const send = useSendMessage();
  const retry = useRetryMessage();
  const edit = useEditMessage(chat.id);
  const remove = useDeleteMessage(chat.id);
  const removeMany = useDeleteMessages(chat.id);
  const react = useToggleReaction(chat.id);
  const report = useReportMessage();
  const { client } = useRealtime();

  const [menuTarget, setMenuTarget] = useState<MenuTarget | null>(null);
  const [pendingDelete, setPendingDelete] = useState<Message | null>(null);
  const [confirmingBulkDelete, setConfirmingBulkDelete] = useState(false);
  const selection = useMessageSelection(viewerUserId);

  const setReference = useComposerStore((state) => state.setReference);
  const startEditing = useComposerStore((state) => state.startEditing);
  const editing = useComposerStore((state) => state.editing[chat.id] ?? null);
  const pendingForChat = usePendingStore((state) => state.byChat[chat.id]);

  const messages = useMemo(() => flattenHistory(history.data), [history.data]);
  const pending = useMemo(
    () =>
      Object.values(pendingForChat ?? {}).sort((left, right) =>
        left.createdAt.localeCompare(right.createdAt),
      ),
    [pendingForChat],
  );

  /**
   * Mark the conversation read once it is on screen.
   *
   * Only the newest message is acknowledged: receipts are monotonic, so one
   * call clears everything below it and a chat with a hundred unread messages
   * does not produce a hundred requests.
   */
  useEffect(() => {
    const newest = messages.at(-1);
    if (!newest || document.hidden) return;
    if (newest.server_sequence <= chat.membership.last_read_sequence) return;
    if (newest.sender_user_id === viewerUserId) return;
    client?.trySend('message.read', { message_id: newest.message_id });
    markChatRead(queryClient, chat.id, newest.server_sequence);
  }, [chat.id, chat.membership.last_read_sequence, client, messages, queryClient, viewerUserId]);


  // Leaving a conversation abandons the choice made in it. Carrying it across
  // would arm a delete against messages the reader can no longer see.
  const clearSelection = selection.clear;
  useEffect(() => {
    clearSelection();
    setConfirmingBulkDelete(false);
  }, [chat.id, clearSelection]);

  const handleSend = useCallback(
    (input: {
      text: string;
      attachment?: { file: UploadFile; fileName: string; previewUrl: string };
    }) => {
      const reference = useComposerStore.getState().references[chat.id] ?? null;
      void send({
        chatId: chat.id,
        deviceId,
        text: input.text,
        reference,
        ...(input.attachment ? { attachment: input.attachment } : {}),
      });
    },
    [chat.id, deviceId, send],
  );

  const closeMenu = useCallback(() => setMenuTarget(null), []);

  const menuActions = menuTarget
    ? {
        react: (emoji: string) => {
          react.mutate({ messageId: menuTarget.message.message_id, emoji });
          closeMenu();
        },
        reply: () => {
          setReference(chat.id, {
            kind: 'reply',
            excerpt: referenceExcerpt(menuTarget.message.content ?? ''),
            message_id: menuTarget.message.message_id,
          });
          closeMenu();
        },
        forward: () => {
          setReference(chat.id, {
            kind: 'forward',
            excerpt: referenceExcerpt(menuTarget.message.content ?? ''),
            message_id: menuTarget.message.message_id,
          });
          closeMenu();
        },
        copy: () => {
          const parsed = parseMessageBody(menuTarget.message.content);
          void navigator.clipboard
            .writeText(parsed.attachmentName ? parsed.caption : parsed.content)
            .then(() => notify('Сообщение скопировано'))
            .catch(() => notify('Не удалось скопировать.', 'error'));
          closeMenu();
        },
        edit: () => {
          const parsed = parseMessageBody(menuTarget.message.content);
          startEditing(chat.id, menuTarget.message.message_id, parsed.content);
          closeMenu();
        },
        select: () => {
          selection.toggle(menuTarget.message);
          closeMenu();
        },
        remove: () => {
          setPendingDelete(menuTarget.message);
          closeMenu();
        },
        report: () => {
          report.mutate(menuTarget.message.message_id);
          closeMenu();
        },
      }
    : null;

  const composerDisabled =
    Boolean(chat.peer_is_blocked) || (chat.kind === 'channel' && !chat.can_publish);

  /**
   * Files dragged onto the conversation.
   *
   * The whole column accepts them, not just the composer: a file is dragged at
   * the conversation it is meant for, and aiming for a strip at the bottom of it
   * is a demand the interface has no reason to make. The file lands in the
   * composer, where it can be given a caption or taken back — dropping is not
   * sending.
   */
  const attach = useAttachFile(chat.id, limits);
  const drop = useFileDrop({
    // Choosing messages or editing one is not a moment when a file can be
    // attached, and offering it anyway would promise something that cannot
    // happen.
    disabled: composerDisabled || selection.selecting || Boolean(editing),
    onMedia: attach,
  });

  return (
    <section
      className="conversation"
      data-chat-kind={chat.kind}
      id="main"
      {...drop.handlers}
    >
      {selection.selecting ? (
        <SelectionHeader
          count={selection.selectedIds.size}
          busy={removeMany.isPending}
          onCancel={selection.clear}
          onDelete={() => setConfirmingBulkDelete(true)}
        />
      ) : (
        <ChatHeader
          chat={chat}
          onBack={onBack}
          onStartCall={onStartCall}
          onOpenDetails={onOpenDetails}
        />
      )}

      <ErrorBoundary area="conversation">
        <MessageList
          chat={chat}
          messages={messages}
          pending={pending}
          viewerUserId={viewerUserId}
          loading={history.isPending}
          hasOlder={Boolean(history.hasNextPage)}
          loadingOlder={history.isFetchingNextPage}
          olderLoaded={(history.data?.pages.length ?? 0) > 1}
          onLoadOlder={() => void history.fetchNextPage()}
          onContextMenu={(message, anchor) => setMenuTarget({ message, anchor })}
          selecting={selection.selecting}
          selectedIds={selection.selectedIds}
          canSelect={selection.canSelect}
          onToggleSelected={selection.toggle}
          onToggleReaction={(messageId, emoji) => react.mutate({ messageId, emoji })}
          onRetry={(messageId) => void retry(chat.id, messageId, deviceId)}
          lastReadSequence={chat.membership.last_read_sequence}
        />
      </ErrorBoundary>

      {selection.selecting ? null : chat.peer_is_blocked ? (
        <div className="channel-subscriber-notice">
          Пользователь заблокирован. Разблокируйте его, чтобы продолжить переписку.
        </div>
      ) : (
        <MessageComposer
          chat={chat}
          disabled={composerDisabled}
          limits={limits}
          onSend={handleSend}
          onSubmitEdit={(messageId, content) => edit.mutate({ messageId, content })}
        />
      )}

      {/*
        Announced to the eye only: dragging a file is a pointer gesture, and
        nobody doing it is reading the page with a screen reader. It never takes
        the pointer either — see the stylesheet.
      */}
      {drop.active ? (
        <div className="conversation-drop" aria-hidden="true">
          <span>
            <Upload size={18} />
            Отпустите файл, чтобы прикрепить его к сообщению
          </span>
        </div>
      ) : null}

      {menuTarget && menuActions ? (
        <MessageContextMenu
          target={menuTarget}
          own={menuTarget.message.sender_user_id === viewerUserId}
          canReply={chat.kind !== 'channel' || chat.can_publish}
          canEdit={menuTarget.message.protocol === 'LINE_SERVER_V1'}
          canSelect={selection.canSelect(menuTarget.message)}
          onClose={closeMenu}
          onReact={menuActions.react}
          onReply={menuActions.reply}
          onForward={menuActions.forward}
          onCopy={menuActions.copy}
          onSelect={menuActions.select}
          onEdit={menuActions.edit}
          onDelete={menuActions.remove}
          onReport={menuActions.report}
        />
      ) : null}

      {pendingDelete ? (
        <ConfirmDialog
          title="Удалить сообщение?"
          description="Сообщение исчезнет у всех участников чата. Отменить это действие нельзя."
          confirmLabel="Удалить"
          destructive
          onCancel={() => setPendingDelete(null)}
          onConfirm={() => {
            remove.mutate(pendingDelete.message_id);
            setPendingDelete(null);
          }}
        />
      ) : null}

      {confirmingBulkDelete ? (
        <ConfirmDialog
          title={
            selection.selectedIds.size === 1
              ? 'Удалить сообщение?'
              : `Удалить сообщений: ${selection.selectedIds.size}?`
          }
          description="Они исчезнут у всех участников чата. Отменить это действие нельзя."
          confirmLabel="Удалить"
          destructive
          onCancel={() => setConfirmingBulkDelete(false)}
          onConfirm={() => {
            removeMany.mutate([...selection.selectedIds]);
            setConfirmingBulkDelete(false);
            selection.clear();
          }}
        />
      ) : null}
    </section>
  );
}


/**
 * The header while messages are being chosen.
 *
 * It replaces the ordinary one rather than extending it: calling, archiving
 * and opening the profile are not what anyone is doing mid-delete, and leaving
 * them a click away from Delete invites the wrong one.
 */
function SelectionHeader({
  count,
  busy,
  onCancel,
  onDelete,
}: {
  count: number;
  busy: boolean;
  onCancel: () => void;
  onDelete: () => void;
}) {
  return (
    <header className="topbar chat-head selection-head">
      <button className="icon-btn" type="button" aria-label="Отменить выбор" onClick={onCancel}>
        <X size={20} />
      </button>
      <strong className="selection-count" aria-live="polite">
        Выбрано: {count}
      </strong>
      <button
        className="icon-btn danger"
        type="button"
        aria-label="Удалить выбранные"
        onClick={onDelete}
        disabled={busy}
      >
        <Trash2 size={20} />
      </button>
    </header>
  );
}

export function EmptyConversation() {
  const searchOpen = useUiStore((state) => state.setSearchOpen);

  return (
    <section className="conversation" id="main">
      <div className="conversation-empty">
        <MessageCircleMore size={42} aria-hidden="true" />
        <h1>Начните общение</h1>
        <p>Выберите чат слева или найдите собеседника по имени, username или email.</p>
        <button type="button" className="secondary-btn" onClick={() => searchOpen(true)}>
          Найти собеседника
        </button>
      </div>
    </section>
  );
}
